import { useCollection, useRefreshCollection, useResources } from 'json-api-hooks';
import React, { useMemo } from 'react';

import ResourceForm, { FormFieldDefinition } from '../components/Form/ResourceForm';
import { InputType } from '../components/Form/types';
import { LoadingCardContent } from '../components/Loading';
import useSnackbar from '../hooks/useSnackbar';
import { AddressResource, SurveyFieldOptionResource, SurveyFieldResource, SurveyFormResource } from '../models';

const buildFormField = (
  surveyField: SurveyFieldResource,
  surveyFields: SurveyFieldResource[],
  surveyFieldOptions: Array<SurveyFieldOptionResource | undefined>,
) => {
  const attrs: FormFieldDefinition = {
    attribute: surveyField.attributes.key as any,
    type: surveyField.attributes.inputType,
    label: surveyField.attributes.label,
    description: surveyField.attributes.description,
    placeholder: surveyField.attributes.placeholder,
    helper: surveyField.attributes.helperText,
    maxInclusive: surveyField.attributes.maxInclusive,
    minInclusive: surveyField.attributes.minInclusive,
    required: surveyField.attributes.required,
    options: surveyField.relationships.surveyFieldOptions.data.map(({ id }) => {
      const surveyFieldOption = surveyFieldOptions.find((option) => option?.id === id);

      return { key: surveyFieldOption?.attributes.label ?? '', label: surveyFieldOption?.attributes.label };
    }),
  };
  if (surveyField.relationships.conditionField.data && surveyField.relationships.conditionFieldOption.data) {
    const conditionField = surveyFields.find((field) => field.id === surveyField.relationships.conditionField.data!.id);
    const conditionFieldOption = surveyFieldOptions.find(
      (option) => option?.id === surveyField.relationships.conditionFieldOption.data!.id,
    );

    if (conditionField && conditionFieldOption) {
      attrs.if = ({ formValues }) => {
        return (
          formValues.surveyAttributes[conditionField.attributes.key as keyof typeof formValues.surveyAttributes] ===
          conditionFieldOption.attributes.label
        );
      };
    }
  }

  return attrs;
};

const useVisitFields = (surveyForm?: SurveyFormResource) => {
  const [surveyFields] = useCollection('survey_fields', { filter: { surveyFormId: [surveyForm?.id ?? ''] } });
  const surveyFieldOptions = useResources(
    surveyFields.flatMap((surveyField) => surveyField.relationships.surveyFieldOptions.data),
  );

  return useMemo<FormFieldDefinition<'visits'>[]>(
    () => [
      {
        attribute: 'addressId',
        required: true,
        type: InputType.Hidden,
      },
      {
        attribute: 'surveyFormId',
        required: true,
        type: InputType.Hidden,
      },
      {
        attribute: 'spokeSomebody',
        label: 'Heb je een gesprek gevoerd?',
        required: true,
        type: InputType.ToggleGroup,
        options: [
          { key: 'spoke_somebody_yes', label: 'Ja' },
          { key: 'spoke_somebody_no', label: 'Nee' },
        ],
      },
      {
        attribute: 'result',
        label: 'Waarom niet?',
        required: true,
        type: InputType.RadioGroup,
        options: [
          { key: 'not_at_home', label: 'Niet thuis' },
          { key: 'come_back', label: 'Nu geen tijd' },
          { key: 'no_dutch', label: 'Gesprek niet mogelijk (bijv. door andere taal)' },
          { key: 'no_conversation', label: 'Geen interesse' },
          { key: 'not_a_home', label: 'Geen woonhuis' },
        ],
        if: ({ formValues }) => formValues.spokeSomebody === 'spoke_somebody_no',
      },
      {
        attribute: 'surveyAttributes',
        nestedFields: surveyFields.map((surveyField) => buildFormField(surveyField, surveyFields, surveyFieldOptions)),
        type: InputType.AssociationInput,
        if: ({ formValues }) => formValues.spokeSomebody === 'spoke_somebody_yes',
      },
    ],
    [surveyFields],
  );
};

const useSurveyForm = (address: AddressResource): SurveyFormResource | undefined => {
  const [surveys] = useCollection('survey_forms');

  return useMemo(() => {
    const matchedSurvey = surveys.find((survey) =>
      survey.attributes.postalRanges.some((range) => {
        const [min, max] = range.split('-');

        return (
          address.attributes.postalDigits >= Number.parseInt(min) &&
          address.attributes.postalDigits <= Number.parseInt(max)
        );
      }),
    );

    return matchedSurvey ?? surveys.find((survey) => survey.attributes.default);
  }, [surveys]);
};

const VisitForm = ({ address, onClose }: { address: AddressResource; onClose: () => void }) => {
  const surveyForm = useSurveyForm(address);
  const fields = useVisitFields(surveyForm);
  const refreshCollection = useRefreshCollection();
  const showSnackbar = useSnackbar();

  const handleSuccess = () => {
    showSnackbar('Bedankt voor je bijdrage!');
    refreshCollection('addresses');
    onClose();
  };

  if (!surveyForm) {
    return <LoadingCardContent />;
  }

  return (
    <ResourceForm
      defaultValues={{ addressId: address.id, surveyFormId: surveyForm.id, surveyAttributes: {} }}
      fields={fields}
      submitLabel="Opslaan"
      type="visits"
      onClose={onClose}
      onSuccess={handleSuccess}
    />
  );
};

export default VisitForm;
